import type { Observation } from '@medplum/fhirtypes'
import { hasCodeableConcept } from '@seen/core/codeableConcept'
import * as codeSystems from '@seen/core/codeSystems'
import { formatDateNumber } from '@seen/core/dates'
import { questionnaireResponseTitle } from '@seen/core/display'
import type { IDT } from '@seen/core/models/care-teams/idt'
import { isSignedCitableSource } from '@seen/core/models/compositionNote'
import type { ExtendedComposition } from '@seen/core/models/compositions/composition'
import type { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import type { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import type { ExtendedImmunization } from '@seen/core/models/immunizations/ExtendedImmunization'
import * as LOINC from '@seen/core/models/loinc'
import type { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import type { ExtendedObservation } from '@seen/core/models/observation'
import type { Order } from '@seen/core/models/orders/Order'
import type { Participant } from '@seen/core/models/patients/participant'
import type { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import { extractId } from '@seen/core/utils'
import {
  getNoteName,
  getNoteSignedOn,
  toDisplayableNote,
} from '#src/utils/noteDisplay.ts'

/* Whichever citable type was read; the others are undefined. */
export interface CitedResources {
  composition?: ExtendedComposition
  questionnaireResponse?: ExtendedQuestionnaireResponse
  documentReference?: ExtendedDocumentReference
  careTeam?: IDT
  observation?: ExtendedObservation
  condition?: Diagnosis
  medicationRequest?: ExtendedMedicationRequest
  serviceRequest?: Order
  immunization?: ExtendedImmunization
  participant?: Participant
  /* The compositions that name the cited response in a section, if any. A
   * validated-tool response lives in a section of the assessment or note that
   * holds it, so that record's signature is what decides it — see below. Empty
   * for every other kind of source, and for a response nothing names. */
  parentCompositions?: ExtendedComposition[]
}

interface SourceDisplay {
  name: string
  signedOn: string | undefined
}

/* What the clinician reads on an AI proposal's source chip — `<name> - <signed
 * date>`, the name being the one the resource wears where a clinician would
 * open it — derived from the cited resource rather than from the label the
 * model wrote. Undefined means no chip at all.
 *
 * The model is asked for a reference and a label; only the reference is
 * trustworthy. A label is free text it composed, so it drifted from the note's
 * real name and from its real date — the same source turned up as
 * "SW Assessment - Social Work" on one proposal, and with the date written two
 * different ways across others. Deriving the label makes the chip's identity
 * structural: it says what the notes list says, or it says nothing.
 *
 * This is also the signature gate. Only a signed resource may stand as evidence
 * behind a proposal — the note tools list colleagues' unsigned drafts on
 * purpose, so without this a proposal could cite work in progress as though it
 * were the record. It asks `isSignedCitableSource`, the same predicate the
 * tools withhold unsigned content by, rather than inferring signature from the
 * presence of a date: FHIR leaves the signing timestamp optional, and a signed
 * record that lacks one is still signed. It then renders its name alone rather
 * than trailing a separator with nothing after it. */
export function suggestionSourceLabel(
  resources: CitedResources
): string | undefined {
  const resource = citedResource(resources)
  if (!resource || !isSignedCitableSource(resource.toFhirResource())) {
    return undefined
  }

  /* A validated-tool response embedded in an assessment or a visit note is
   * judged by that record, not by its own status: `completed` says the form was
   * filled in, which happens long before anyone signs the record around it.
   * Same rule the tools withhold its answers by, so a response the model cannot
   * read is a response it cannot cite either. A response nothing names — a
   * standalone tool, or the care plan's own — has no parent and keeps being
   * judged on its own status. */
  const hasUnsignedParent = (resources.parentCompositions ?? []).some(
    (parent) => !isSignedCitableSource(parent.toFhirResource())
  )
  if (hasUnsignedParent) {
    return undefined
  }
  const display = describe(resources)
  if (!display) {
    return undefined
  }
  return display.signedOn
    ? `${display.name} - ${formatDateNumber(display.signedOn)}`
    : display.name
}

function citedResource(resources: CitedResources) {
  return (
    resources.composition ??
    resources.questionnaireResponse ??
    resources.documentReference ??
    resources.careTeam ??
    resources.observation ??
    resources.condition ??
    resources.medicationRequest ??
    resources.serviceRequest ??
    resources.immunization ??
    resources.participant
  )
}

function describe({
  composition,
  questionnaireResponse,
  documentReference,
  careTeam,
  observation,
  condition,
  medicationRequest,
  serviceRequest,
  immunization,
  participant,
}: CitedResources): SourceDisplay | undefined {
  if (composition) {
    /* The notes list's own derivation, so a chip and a row name the same note
     * identically. A Composition that list does not show — a care-plan
     * snapshot — is not a note to cite either. */
    const note = toDisplayableNote(composition.toFhirResource())
    return note
      ? { name: getNoteName(note), signedOn: getNoteSignedOn(note) }
      : undefined
  }
  if (questionnaireResponse) {
    /* Validated tools carry a name of their own; every other response is named
     * by the questionnaire it answered. `authored` is the only signing a
     * response has, and it is read off the raw resource because FHIR leaves it
     * optional where the model's getter asserts it. */
    const resource = questionnaireResponse.toFhirResource()
    const name =
      questionnaireResponse.name ?? questionnaireResponseTitle(resource)
    return name ? { name, signedOn: resource.authored } : undefined
  }
  if (documentReference) {
    /* A document's date is the service date — when the care described happened
     * — because that is what a citation claims; `date` is the ingestion
     * timestamp, which for a scanned outside record can post-date the visit by
     * months. Outside records often arrive with no determinable service date,
     * and a confirmed document must still get a chip, so ingestion stands in
     * when there is nothing better. */
    return {
      name: documentReference.displayTitle,
      signedOn:
        documentReference.serviceDateString ??
        documentReference.dateStringWithFallback,
    }
  }
  /* The quote under the chip carries the structured record's fact, so the chip
   * names only the part of the chart it came from. */
  if (careTeam) {
    return { name: 'Care team', signedOn: undefined }
  }
  if (condition) {
    return { name: 'Problem list', signedOn: undefined }
  }
  if (medicationRequest) {
    return { name: 'Medications', signedOn: undefined }
  }
  if (immunization) {
    return { name: 'Immunizations', signedOn: undefined }
  }
  if (participant) {
    return { name: 'Demographics', signedOn: undefined }
  }
  if (serviceRequest) {
    return {
      name: 'Orders',
      signedOn: serviceRequest.toFhirResource().authoredOn,
    }
  }
  if (observation) {
    const resource = observation.toFhirResource()
    return {
      name: isVitalSign(resource)
        ? 'Vitals'
        : (observation.display ?? 'Observation'),
      signedOn:
        resource.effectiveDateTime ??
        resource.effectiveInstant ??
        resource.effectivePeriod?.start,
    }
  }
  return undefined
}

/* The vitals an Orbit or Elation note records. Neither writes the standard
 * `vital-signs` category, so the LOINC code is what identifies one; the
 * category is still honoured for a record that does carry it. */
const VITAL_SIGN_CODINGS = [
  LOINC.BLOOD_PRESSURE_CODING,
  LOINC.SYSTOLIC_BLOOD_PRESSURE_CODING,
  LOINC.DIASTOLIC_BLOOD_PRESSURE_CODING,
  LOINC.HEART_RATE_CODING,
  LOINC.RESPIRATORY_RATE_CODING,
  LOINC.BODY_TEMPERATURE_CODING,
  LOINC.OXYGEN_SATURATION_CODING,
  LOINC.BODY_HEIGHT_CODING,
  LOINC.BODY_WEIGHT_CODING,
  LOINC.BODY_MASS_INDEX_CODING,
  LOINC.HEAD_CIRCUMFERENCE_CODING,
  LOINC.PAIN_SEVERITY_CODING,
]

function isVitalSign(observation: Observation): boolean {
  const vitalSignsCategory = observation.category?.some((category) =>
    category.coding?.some(
      (coding) =>
        coding.system === codeSystems.HL7_OBSERVATION_CODE_SYSTEM &&
        coding.code === 'vital-signs'
    )
  )
  return (
    !!vitalSignsCategory ||
    VITAL_SIGN_CODINGS.some((coding) =>
      hasCodeableConcept(observation.code, coding)
    )
  )
}

/* The participant the cited resource is about, as `Patient/<id>`. Read off the
 * raw resource rather than through the models' `subject` getters: some declare
 * it optional and others assert, so the getters do not agree on what a missing
 * subject means. Undefined means nothing has resolved yet, or
 * the resource carries no subject at all. An immunization names its participant
 * as `patient`, and a participant record is the participant. */
export function citedSourceSubject(
  resources: CitedResources
): string | undefined {
  const resource = citedResource(resources)?.toFhirResource()
  if (!resource) {
    return undefined
  }
  switch (resource.resourceType) {
    case 'Immunization':
      return resource.patient?.reference
    case 'Patient':
      return resource.id ? `Patient/${resource.id}` : undefined
    default:
      return resource.subject?.reference
  }
}

/* Whether the cited resource is CONFIRMED to be about the open participant.
 *
 * Fails closed on anything it cannot confirm, which is the whole point: a
 * resource carrying no subject is not a resource belonging to nobody, it is one
 * whose participant is unknown. An unfiled inbox document is the ordinary case
 * — it sits unlinked precisely because which chart it belongs to has not been
 * established yet, and often it turns out to be someone else's. Rendering one
 * as evidence on the open chart asserts a link the record does not make.
 *
 * A surface with no participant in context cannot confirm anything either, so
 * it gets the same answer. This matches the server-side rule the note tool
 * already applies, where a note whose subject cannot be shown to be the open
 * participant is refused rather than returned. */
export function isCitedSourceOnChart(
  resources: CitedResources,
  participantReference: string | undefined
): boolean {
  const subject = citedSourceSubject(resources)
  if (!subject || !participantReference) {
    return false
  }
  return extractId(subject) === extractId(participantReference)
}
